import _ from 'underscore';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import type { PaymentInstrument, PaymentRepositories } from './types';

const { AllowedAgencyPITypes } = Constants;

/**
 * Verify SAP Customer is unique
 * @param {object} jsDataCcRest jsData object
 * @param {string} legalBusinessName the legal business name
 * @param {string} countryCode the country code, eg: US
 * @returns {promise<boolean>} a boolean indicating if the SAP customer is unique
 */
export const isSAPCustomerUnique = (jsDataCcRest: PaymentRepositories, legalBusinessName: string, countryCode: string) => {
  const id = `${legalBusinessName}_${countryCode}`;
  return jsDataCcRest.VerifySAPCustomerUniqueness.find(id, {
    params: {
      legalBusinessName,
      countryCode,
    },
    bypassCache: true,
  }).then(({
    value
  }: { value: boolean }) => value);
};

/**
 * Returns a list of payment instruments for the given agency customer id
 * @function
 * @param {object} jsDataCcRest jsData object
 * @param {number} agencyCustomerId The Id of the agency
 * @return {Promise<Array.<object>>} The payment instrument list
 */
export const getAgencyPaymentInstruments = (jsDataCcRest: PaymentRepositories, agencyCustomerId: number) => jsDataCcRest.PaymentInstrument
  .findAll({ customerId: agencyCustomerId })
  .then((paymentInstruments: PaymentInstrument[]) => _.chain(paymentInstruments)
    .filter((pi: PaymentInstrument) => _.contains(AllowedAgencyPITypes, pi.Type))
    .value());


/**
 * Returns the account's primary payment instrument
 * For some cases MT will throw error, so `catch` is needed
 * @function
 * @param {object} jsDataCcRest jsData object
 * @param {object} args The argument
 * @property {number} accountId The Id of the account
 * @return {Promise<object>} The payment instrument
 */
export const getAccountPrimaryPaymentInstr = (jsDataCcRest: PaymentRepositories, {
  accountId
}: { accountId: number }) => jsDataCcRest.Account.find(accountId)
  .then((account) => {
    if (account && account.PreferredBillToPaymentInstrumentId > 0) {
      return jsDataCcRest.PaymentInstrument.find(account.PreferredBillToPaymentInstrumentId);
    }

    return null;
  })
  .catch((): null => null);
